"use client";

import { useLayoutEffect, useSyncExternalStore, type ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import {
  AUTH_TOKEN_KEY,
  getAuthSessionSnapshot,
  getServerAuthSnapshot,
  refreshAuthSessionFromStorage,
  setAuthSessionCookiePresent,
  subscribeAuthSession,
} from "./auth-session";

function loginPathWithReturn(pathname: string, search: string): string {
  const from = `${pathname}${search}`;
  if (!from || from === "/login" || from.startsWith("/login?")) return "/login";
  return `/login?from=${encodeURIComponent(from)}`;
}

/** Private routes: stay on the current URL when a JWT exists in localStorage. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const token = useSyncExternalStore(
    subscribeAuthSession,
    () => getAuthSessionSnapshot().token,
    () => getServerAuthSnapshot().token,
  );

  useLayoutEffect(() => {
    refreshAuthSessionFromStorage();
    const stored = window.localStorage.getItem(AUTH_TOKEN_KEY)?.trim();
    if (stored) {
      setAuthSessionCookiePresent();
      return;
    }
    const search = searchParams.toString();
    router.replace(loginPathWithReturn(pathname, search ? `?${search}` : ""));
  }, [router, pathname, searchParams]);

  if (!token) return null;
  return children;
}
